import type { ForwardedRef, FunctionComponent, InputHTMLAttributes, JSX } from "react";
import { forwardRef, useEffect, useState } from "react";

import type { SVGIconProps } from "@repo/icons/interface";

import { Button } from "@/components/ui/button/button";
import {
  SelectDrawer,
  SelectDrawerBodyListRadio,
  SelectDrawerBodySingleList,
  SelectDrawerFooter,
  type SelectDrawerItemProps,
  SelectDrawerSearch,
  SelectDrawerTitle,
} from "@/components/ui/drawer/select-drawer";
import {
  InputSelect,
  InputSelectContent,
  InputSelectTitle,
} from "@/components/ui/input/input-select";

export interface FallbackProps<T extends string | number> {
  input: string;
  setSelectedItem: (item: SelectDrawerItemProps<T>) => void;
  closeFallbackComponent: () => void;
}

export interface SelectInputProps<T extends string | number>
  extends InputHTMLAttributes<HTMLInputElement> {
  value?: T;
  onChange: (event: React.ChangeEvent<HTMLInputElement> | T) => void;
  items: SelectDrawerItemProps<T>[];
  searchable?: boolean;
  required?: boolean;
  title: string;
  drawerTitle?: string;
  placeholder?: string;
  Icon?: FunctionComponent<SVGIconProps>;
  name: string;
  hasConfirmButton?: boolean;
  fallbackButtonLabel?: string;
  FallbackComponent?: (props: FallbackProps<T>) => JSX.Element;
}

export const SelectInputInner = <T extends string | number>(
  {
    id,
    name,
    title,
    drawerTitle = title,
    value,
    onChange,
    items,
    searchable,
    placeholder = "Selecione",
    hasConfirmButton = false,
    fallbackButtonLabel,
    FallbackComponent,
    ...props
  }: SelectInputProps<T>,
  ref: ForwardedRef<HTMLInputElement>,
): JSX.Element => {
  const initialSelectedItem = items.find((x) => x.value === value);
  const [showDrawer, setShowDrawer] = useState(false);
  const [searchResults, setSearchResults] = useState<SelectDrawerItemProps<T>[]>(items);
  const [selectedItem, setSelectedItem] = useState<SelectDrawerItemProps<T> | undefined>(
    initialSelectedItem ?? undefined,
  );
  const [showFallbackComponent, setShowFallbackComponent] = useState(false);
  const [inputValue, setInputValue] = useState("");

  const commitChangesAndClose = (): void => {
    if (!selectedItem) return;
    onChange(selectedItem.value);
    setShowDrawer(false);
  };

  // TODO: Refatorar esse useEffect posteriormente para uma solução mais elegante
  useEffect(() => {
    setSearchResults(items);
  }, [items]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: legacy code
  useEffect(() => {
    if (!hasConfirmButton && selectedItem && selectedItem !== initialSelectedItem) {
      onChange(selectedItem.value);
      setShowDrawer(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- Efeito executado quando um item for selecionado.
  }, [selectedItem]);

  const filterUniqueValues = (
    item: SelectDrawerItemProps<T>,
    index: number,
    array: SelectDrawerItemProps<T>[],
  ): boolean => {
    const itemIndex = array.findIndex((b) => item.value === b.value);
    return index === itemIndex;
  };

  const handleFallbackSelectedItem = (item: SelectDrawerItemProps<T>): void => {
    setSelectedItem(item);
    onChange(item.value);
  };

  return (
    <>
      <InputSelect required={props.required}>
        <InputSelectTitle>{title}</InputSelectTitle>
        <InputSelectContent
          id={id}
          name={name}
          disabled={props.disabled}
          value={value}
          onClick={() => setShowDrawer(true)}
          placeholder={placeholder}
          label={initialSelectedItem?.label}
          ref={ref}
          Icon={props.Icon}
          required={props.required}
        />
      </InputSelect>
      <SelectDrawer open={showDrawer} setOpen={setShowDrawer}>
        <SelectDrawerTitle>{drawerTitle}</SelectDrawerTitle>
        {searchable ? (
          <SelectDrawerSearch
            items={items}
            onSearch={setSearchResults}
            onChange={(valueFromEvent: string) => setInputValue(valueFromEvent)}
          />
        ) : null}
        {showFallbackComponent && FallbackComponent ? (
          <FallbackComponent
            input={inputValue}
            setSelectedItem={(item: SelectDrawerItemProps<T>) =>
              handleFallbackSelectedItem(item)
            }
            closeFallbackComponent={() => setShowFallbackComponent(false)}
          />
        ) : (
          <div>
            <SelectDrawerBodySingleList
              initialSelectedItem={initialSelectedItem}
              setSelectedItem={setSelectedItem}
            >
              {searchResults.filter(filterUniqueValues).map((item) => (
                <SelectDrawerBodyListRadio
                  value={item.value}
                  key={`searchResult-${item.label}-${item.value}`}
                  description={item.description}
                  disabled={item.disabled}
                >
                  {item.label}
                </SelectDrawerBodyListRadio>
              ))}
            </SelectDrawerBodySingleList>
            {hasConfirmButton ? (
              <SelectDrawerFooter>
                <Button
                  onClick={commitChangesAndClose}
                  disabled={!selectedItem}
                  type="button"
                  fullWidth
                >
                  Confirmar
                </Button>
              </SelectDrawerFooter>
            ) : null}
            {fallbackButtonLabel ? (
              <Button fullWidth onClick={() => setShowFallbackComponent(true)}>
                {fallbackButtonLabel}
              </Button>
            ) : null}
          </div>
        )}
      </SelectDrawer>
    </>
  );
};
export const SelectInput = forwardRef(SelectInputInner) as <T extends string | number>(
  p: SelectInputProps<T> & { ref?: ForwardedRef<HTMLInputElement> },
) => ReturnType<typeof SelectInputInner>;
